import { Box, Grid, Button, Typography, IconButton, Tooltip, Stack, Popover } from "@mui/material";
import {
  CallEnd,
  Mic,
  MicOff,
  PauseRounded,
  PlayArrowRounded,
  PhoneForwardedRounded,
  PersonAdd,
} from "@mui/icons-material";
import { useCallback, useMemo, useState } from "react";
import { dialPad } from "../const/dialPad";
import { useCallModals } from "../hooks/useCallModals";
import { useVirtualAssistant, CompanyRuntimeState } from "../../context/VirtualAssistantContext";
import { useModals } from "../../../../context/ModalContext";
import { formatUSPhone } from "../../../../utils";
import { BaseModalComponent } from "../../../common/BaseModalComponent";
import { baseColor } from "../../VirtualReceptionistView";
import { colors, alphaVar } from "../../../ui/colors";
import { useCallerContactsQuery } from "../hooks/useCallerContactsQuery";
import { TransferPanel } from "./transfer/TransferPanel";
import { useOptionalInternalWebRTC } from "../../../internal-contacts/_context/InternalWebRTCContext";
import { useOptionalInternalTwilioCall } from "../../../internal-twilio-call/context/InternalTwilioCallContext";
import { useVrBulkResolver } from "@components/virtual-receptionist/contacts/hooks/useBulkContactResolver";
import { useBulkResolveContacts } from "@components/virtual-receptionist/contacts/hooks/useBulkResolveContacts";
import { getCallerType, getDisplayName, getRoleLabel, findResolvedByPhone } from "@components/virtual-receptionist/contacts/types/bulk-resolver.types";

const getCompanyIdFromDeviceKey = (
  deviceKey?: string,
): string | null => {
  if (!deviceKey) return null;

  const parts = deviceKey.split(":");

  // New format: sessionId:companyId:phoneNumber
  if (parts.length >= 3) return parts[1] || null;

  // Old format: companyId:phoneNumber
  if (parts.length >= 2) return parts[0] || null;

  return null;
};

const getFullPhone = (
  dialCode?: string | null,
  phoneNumber?: string | null,
): string | null => {
  if (!phoneNumber) return null;

  const cleanPhone = phoneNumber.trim();
  const cleanDialCode = dialCode?.trim();

  if (cleanPhone.startsWith("+")) return cleanPhone;

  return cleanDialCode ? `${cleanDialCode}${cleanPhone}` : cleanPhone;
};

const getResolvedDisplayName = (resolved: any): string | null => {
  if (!resolved) return null;

  return (
    getDisplayName(resolved) ||
    resolved.displayName ||
    resolved.fullName ||
    resolved.name ||
    null
  );
};

const getResolvedLabel = (resolved: any): string | null => {
  if (!resolved) return null;

  const label = getRoleLabel(resolved);

  if (label) return label;

  if (resolved.callerType === "client") return "Client";
  if (resolved.callerType === "workforce") return "Workforce";

  return null;
};

const getResolvedCallerType = (
  resolved: any,
): "client" | "workforce" | null => {
  if (!resolved) return null;

  if (resolved.callerType === "client") return "client";
  if (resolved.callerType === "workforce") return "workforce";

  return getCallerType(resolved);
};

const MODALS_HEIGHT = 64;
const ENABLE_IN_CALL_MOCK = false;

interface Props {
  deviceKey?: string;  // "companyId:phoneNumber"
  forceChannel?: "webrtc" | "twilio";
}

export const InCall = ({ deviceKey, forceChannel }: Props) => {
  const internalWebRTC = useOptionalInternalWebRTC();
  const internalTwilio = useOptionalInternalTwilioCall();

  if (forceChannel === "twilio") {
    return <TwilioInCall deviceKey={deviceKey} />;
  }

  if (forceChannel === "webrtc") {
    return <InternalWebRTCInCall />;
  }

  if (internalTwilio?.internalCallView === "in-call") {
    return <InternalTwilioInCall />;
  }

  if (internalWebRTC?.internalCallView === "in-call") {
    return <InternalWebRTCInCall />;
  }

  return <TwilioInCall deviceKey={deviceKey} />;
};

const InternalWebRTCInCall = () => {
  const {
    peerDisplayName,
    peerSubtitle,
    callDuration,
    isMuted,
    hangup,
    toggleMute,
  } = useInternalWebRTCRequired();

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, "0");
    const s = (seconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="space-between"
      sx={{ overflow: "hidden", height: "100%", width: "100%" }}
    >
      <Box sx={{ textAlign: "center", width: "100%" }}>
        <Typography variant="h4" sx={{ fontWeight: 300 }}>
          {formatTime(callDuration)}
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 500, mt: 0.5 }}>
          {peerDisplayName}
        </Typography>

        {peerSubtitle && (
          <Typography
            variant="body1"
            sx={{ mt: 0.75, fontWeight: 600, color: colors.orange.base }}
          >
            {peerSubtitle}
          </Typography>
        )}

        <Typography variant="caption" color="text.secondary">
          In call
        </Typography>
      </Box>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 4 }}>
        <Tooltip title={isMuted ? "Unmute" : "Mute"}>
          <IconButton
            onClick={toggleMute}
            sx={{
              width: 56,
              height: 56,
              bgcolor: isMuted ? colors.danger.base : alphaVar(colors.gray[500], 0.12),
              color: isMuted ? "#fff" : colors.gray[700],
            }}
          >
            {isMuted ? <MicOff /> : <Mic />}
          </IconButton>
        </Tooltip>

        <Tooltip title="Hang up">
          <IconButton
            onClick={() => void hangup()}
            sx={{
              width: 64,
              height: 64,
              bgcolor: colors.danger.base,
              color: "#fff",
              "&:hover": { bgcolor: colors.danger[700] },
            }}
          >
            <CallEnd sx={{ fontSize: 28 }} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
};

function useInternalWebRTCRequired() {
  const ctx = useOptionalInternalWebRTC();
  if (!ctx) throw new Error("Internal WebRTC context is required");
  return ctx;
}

const InternalTwilioInCall = () => {
  const ctx = useOptionalInternalTwilioCall();
  if (!ctx) return null;

  const { peerDisplayName, callDuration, isMuted, hangupCall, toggleMute } = ctx;

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, "0");
    const s = (seconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="space-between"
      sx={{ overflow: "hidden", height: "100%", width: "100%" }}
    >
      <Box sx={{ textAlign: "center", width: "100%" }}>
        <Typography variant="h4" sx={{ fontWeight: 300 }}>
          {formatTime(callDuration)}
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 500, mt: 0.5 }}>
          {peerDisplayName}
        </Typography>

        <Typography variant="caption" color="text.secondary">
          In call
        </Typography>
      </Box>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 4 }}>
        <Tooltip title={isMuted ? "Unmute" : "Mute"}>
          <IconButton
            onClick={toggleMute}
            sx={{
              width: 56,
              height: 56,
              bgcolor: isMuted ? colors.danger.base : alphaVar(colors.gray[500], 0.12),
              color: isMuted ? "#fff" : colors.gray[700],
            }}
          >
            {isMuted ? <MicOff /> : <Mic />}
          </IconButton>
        </Tooltip>

        <Tooltip title="Hang up">
          <IconButton
            onClick={() => hangupCall()}
            sx={{
              width: 64,
              height: 64,
              bgcolor: colors.danger.base,
              color: "#fff",
              "&:hover": { bgcolor: colors.danger[700] },
            }}
          >
            <CallEnd sx={{ fontSize: 28 }} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
};

const TwilioInCall = ({ deviceKey }: Props) => {
  const {
    currentCompanyState,
    activeCallCompanyName,
    getCompanyCallState,
    hangupCall,
    toggleMute,
    sendDigit,
    callDuration,
    isMuted,
    hangupCallByCompany,
    toggleMuteByCompany,
    sendDigitByCompany,
    openAddContactMenu,
    openContactDetails,
  } = useVirtualAssistant();

  const { closeAllModalsStartWith } = useModals();
  const { toggleHold, toggleHoldByCompany } = useVirtualAssistant();
  const [transferAnchorEl, setTransferAnchorEl] =
    useState<HTMLButtonElement | null>(null);

  const isTransferOpen = Boolean(transferAnchorEl);

  const callState = useMemo<CompanyRuntimeState | null>(() => {
    if (ENABLE_IN_CALL_MOCK) {
      return {
        companyId: deviceKey?.split(":")[0] || "106",
        sessionNumber: "",
        status: "ready",
        activeCall: null,
        callDirection: null,
        remotePhone: { dialCode: "+1", phone: "2134747207" },
        callDuration: 143,
        isMuted: false,
        isOnHold: false,
        isInternalIncoming: false,
        internalCallerName: null,
        internalCallerCompanyName: null,
      };
    }

    if (deviceKey) return getCompanyCallState(deviceKey);

    return currentCompanyState;
  }, [deviceKey, getCompanyCallState, currentCompanyState]);

  const remotePhone = callState?.remotePhone;
  const localCallDuration = callState?.callDuration || 0;
  const localIsMuted = callState?.isMuted || false;
  const isOnHold = callState?.isOnHold || false;

  const handleToggleHold = useCallback(() => {
    if (deviceKey) {
      toggleHoldByCompany(deviceKey);
      return;
    }
    toggleHold();
  }, [deviceKey, toggleHoldByCompany, toggleHold]);

  const rawCompanyId =
    getCompanyIdFromDeviceKey(deviceKey) ||
    callState?.companyId ||
    currentCompanyState?.companyId;

  const resolvedCompanyId =
    rawCompanyId !== undefined && rawCompanyId !== null
      ? String(rawCompanyId)
      : null;

  const dialCode = remotePhone?.dialCode;
  const phoneNumber = remotePhone?.phone;
  const fullPhone = getFullPhone(dialCode, phoneNumber);

  const { data: legacyResolvedMap = {}, isLoading: isLoadingLegacyResolved } =
    useBulkResolveContacts(
      !ENABLE_IN_CALL_MOCK && fullPhone ? [fullPhone] : [],
    );

  const legacyResolved =
    !ENABLE_IN_CALL_MOCK && fullPhone
      ? findResolvedByPhone(legacyResolvedMap, fullPhone)
      : null;

  const shouldResolveFromVrContacts =
    !ENABLE_IN_CALL_MOCK &&
    !!fullPhone &&
    !!resolvedCompanyId;

  const {
    data: vrResolvedMap = {},
    isLoading: isLoadingVrResolved,
  } = useVrBulkResolver(
    resolvedCompanyId,
    shouldResolveFromVrContacts ? [fullPhone] : [],
    shouldResolveFromVrContacts,
  );

  const vrResolved =
    !ENABLE_IN_CALL_MOCK && fullPhone
      ? findResolvedByPhone(vrResolvedMap, fullPhone)
      : null;

  /**
   * Logic source:
   * - Legacy resolver still has priority for modals/business logic.
   *
   * Display source:
   * - VR contact can override visual fields like name, lastName, type, etc.
   * - This allows names saved in VR contacts, like "Jocelyn Kirk", to appear.
   */
  const resolvedContact = legacyResolved || vrResolved || null;

  const displayResolvedContact =
    legacyResolved && vrResolved
      ? {
        ...legacyResolved,
        ...vrResolved,
      }
      : resolvedContact;

  console.log("[TwilioInCall][phone-debug]", {
    deviceKey,
    resolvedCompanyId,
    remotePhone,
    dialCode,
    phoneNumber,
    fullPhone,
  });

  console.log("[TwilioInCall][legacy-resolver]", {
    isLoadingLegacyResolved,
    legacyResolvedMap,
    legacyResolved,
    legacyKeys: Object.keys(legacyResolvedMap || {}),
  });

  console.log("[TwilioInCall][vr-resolver]", {
    shouldResolveFromVrContacts,
    isLoadingVrResolved,
    vrResolvedMap,
    vrResolved,
    vrKeys: Object.keys(vrResolvedMap || {}),
  });

  console.log("[TwilioInCall][final-resolved]", {
    resolvedContact,
    displayResolvedContact,
    displayName: getResolvedDisplayName(displayResolvedContact),
    callerType: getResolvedCallerType(resolvedContact),
    callerLabel: getResolvedLabel(displayResolvedContact),
  });

  const mockDisplayName = "John Doe";
  const mockCallerType = "client";
  const mockCallerLabel = "Client";

  const resolvedDisplayName = callState?.isInternalIncoming
    ? callState.internalCallerName
    : getResolvedDisplayName(displayResolvedContact);
  const resolvedCallerType = callState?.isInternalIncoming ? "workforce" : getResolvedCallerType(resolvedContact);
  const resolvedCallerLabel = callState?.isInternalIncoming ? "Internal" : getResolvedLabel(displayResolvedContact);

  const effectiveDisplayName = ENABLE_IN_CALL_MOCK
    ? mockDisplayName
    : resolvedDisplayName;

  const effectiveCallerType = ENABLE_IN_CALL_MOCK
    ? mockCallerType
    : resolvedCallerType;

  const effectiveCallerLabel = ENABLE_IN_CALL_MOCK
    ? mockCallerLabel
    : resolvedCallerLabel;

  const caller = ENABLE_IN_CALL_MOCK ? { uid: "mock-caller" } : resolvedContact;
  const isKnown = ENABLE_IN_CALL_MOCK ? true : !!caller;

  const { modals } = useCallModals(effectiveCallerType, caller);

  const contactForDetails = useMemo(() => {
    if (ENABLE_IN_CALL_MOCK || !fullPhone) return null;

    if (legacyResolved) {
      return {
        ...legacyResolved,
        ...(vrResolved || {}),
        uid: legacyResolved.uid || (vrResolved as any)?.uid,
        name: (legacyResolved as any).firstName || (legacyResolved as any).name || (vrResolved as any)?.name,
        lastName: (legacyResolved as any).lastName || (vrResolved as any)?.lastName,
        type: (legacyResolved as any).type || (vrResolved as any)?.type,
        role: (legacyResolved as any).role || (vrResolved as any)?.role,
        isResolved: true,
      };
    }

    if (vrResolved) {
      return {
        ...vrResolved,
        name: (vrResolved as any).firstName || (vrResolved as any).name,
        lastName: (vrResolved as any).lastName,
      };
    }

    return null;
  }, [fullPhone, legacyResolved, vrResolved]);

  const handleOpenContactSheet = () => {
    if (!fullPhone || !contactForDetails) return;
    openContactDetails(fullPhone, contactForDetails as any);
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");

    const s = (seconds % 60).toString().padStart(2, "0");

    return `${m}:${s}`;
  };

  const onHangupCall = () => {
    closeAllModalsStartWith("Call");

    if (ENABLE_IN_CALL_MOCK) return;

    if (deviceKey) {
      hangupCallByCompany(deviceKey);
      return;
    }

    hangupCall();
  };

  const onToggleMute = () => {
    if (ENABLE_IN_CALL_MOCK) return;

    if (deviceKey) {
      toggleMuteByCompany(deviceKey);
      return;
    }

    toggleMute();
  };

  const onSendDigit = (digit: string) => {
    if (ENABLE_IN_CALL_MOCK) return;

    if (deviceKey) {
      sendDigitByCompany(deviceKey, digit);
      return;
    }

    sendDigit(digit);
  };

  const onOpenTransfer = (event: React.MouseEvent<HTMLButtonElement>) => {
    setTransferAnchorEl(event.currentTarget);
  };

  const onCloseTransfer = () => {
    setTransferAnchorEl(null);
  };

  const formattedNumber = (() => {
    if (callState?.isInternalIncoming) {
      return callState.internalCallerCompanyName || "";
    }

    if (!phoneNumber || !dialCode) return "Unknown number";

    return dialCode === "+1"
      ? formatUSPhone(phoneNumber)
      : `${dialCode} ${phoneNumber}`;
  })();

  const muted = deviceKey ? localIsMuted : isMuted;
  const duration = deviceKey ? localCallDuration : callDuration;

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="space-between"
        sx={{ overflow: "hidden", height: "100%", width: "100%" }}
      >
        <Box sx={{ textAlign: "center", width: "100%" }}>
          <Typography variant="h4" sx={{ fontWeight: 300 }}>
            {formatTime(duration)}
          </Typography>

          {effectiveDisplayName ? (
            <Typography
              variant="h6"
              onClick={contactForDetails ? handleOpenContactSheet : undefined}
              sx={{
                fontWeight: 500,
                mt: 0.5,
                cursor: contactForDetails ? "pointer" : "default",
                "&:hover": contactForDetails
                  ? {
                      color: colors.orange.base,
                      textDecoration: "underline",
                    }
                  : undefined,
              }}
            >
              {effectiveDisplayName}
              {effectiveCallerLabel && (
                <>
                  {" - "}
                  <Box
                    component="span"
                    sx={{
                      fontSize: "1rem",
                      color: "grey.700",
                      fontWeight: 600,
                      ml: 0.5,
                    }}
                  >
                    ({effectiveCallerLabel})
                  </Box>
                </>
              )}
            </Typography>
          ) : (
            <Box
              sx={{
                mt: 0.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                Unknown
              </Typography>
              {fullPhone && (
                <IconButton
                  size="small"
                  aria-label="Add contact"
                  onClick={(e) => openAddContactMenu(fullPhone, e.currentTarget)}
                  sx={{
                    p: 0.35,
                    color: "text.secondary",
                    "&:hover": {
                      color: colors.orange.base,
                      bgcolor: alphaVar(colors.orange.base, 0.08),
                    },
                  }}
                >
                  <PersonAdd sx={{ fontSize: 18 }} />
                </IconButton>
              )}
            </Box>
          )}

          <Typography
            variant="h6"
            sx={{
              letterSpacing: 1.5,
              my: 0.5,
              fontWeight: 600,
              color: colors.orange.base,
            }}
          >
            {formattedNumber}
          </Typography>

          <Typography variant="caption" color="text.secondary">
            {isOnHold ? "On hold" : "In call"}
          </Typography>

          {!deviceKey && activeCallCompanyName && (
            <Box
              sx={{
                mt: 1.25,
                mx: "auto",
                px: 1.5,
                py: 0.9,
                width: "fit-content",
                borderRadius: 99,
                background: alphaVar(colors.orange.base, 0.08),
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  color: colors.orange.base,
                }}
              >
                Call from {activeCallCompanyName}
              </Typography>
            </Box>
          )}

          {isOnHold && (
            <Box
              sx={{
                mt: 1.25,
                mx: "auto",
                px: 1.5,
                py: 0.9,
                width: "fit-content",
                borderRadius: 99,
                background: alphaVar(colors.blue?.[500] ?? "#2563eb", 0.08),
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  color: colors.blue?.[700] ?? "#1d4ed8",
                }}
              >
                Caller is waiting on the line
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              justifyContent: "center",
              minHeight: MODALS_HEIGHT,
              pb: isKnown ? 4 : 0,
              color: baseColor,
              mt: 0.5,
            }}
          >
            {isKnown &&
              Object.values(modals).map((modal) => {
                const ModalComponent = modal.component;

                return (
                  <BaseModalComponent
                    key={modal.key}
                    modalKey={modal.key}
                    modalLabel={modal.label}
                    defaultSizes={modal.defaultSizes}
                    elementTrigger={
                      <Tooltip title={modal.tooltip} arrow placement="top">
                        <Button
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: "50%",
                            minWidth: 0,
                            bgcolor: "action.hover",
                            "&:hover": {
                              bgcolor: colors.orange[100],
                            },
                          }}
                        >
                          {modal.icon}
                        </Button>
                      </Tooltip>
                    }
                  >
                    <ModalComponent {...modal.props} />
                  </BaseModalComponent>
                );
              })}
          </Box>
        </Box>
      </Box>

      <Box sx={{ width: "100%", maxWidth: 320, marginTop: { xs: -2, sm: -5 } }}>
        <Grid container spacing={1.5} sx={{ mt: 1 }}>
          {dialPad.map(({ digit, letters }) => (
            <Grid item xs={4} key={digit}>
              <Button
                onClick={() => onSendDigit(digit)}
                sx={{
                  width: "100%",
                  height: 56,
                  borderRadius: "50%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  minWidth: 0,
                  color: colors.orange.base,
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 800, fontSize: "1.4rem" }}
                >
                  {digit}
                </Typography>

                {letters && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.primary",
                      fontWeight: 700,
                      fontSize: "0.6rem",
                      mt: -0.5,
                    }}
                  >
                    {letters}
                  </Typography>
                )}
              </Button>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box sx={{ width: "100%", maxWidth: 360, mt: 2.5 }}>
        <Stack
          direction="row"
          spacing={1}
          justifyContent="center"
          sx={{ mb: 1.5 }}
        >
          <Button
            onClick={onToggleMute}
            startIcon={
              muted ? (
                <MicOff sx={{ fontSize: "1rem" }} />
              ) : (
                <Mic sx={{ fontSize: "1rem" }} />
              )
            }
            sx={{
              minWidth: 96,
              minHeight: 40,
              px: 1.5,
              color: muted ? colors.danger.base : colors.gray[700],
              fontWeight: 600,
              fontSize: "0.84rem",
              textTransform: "none",
              borderRadius: 2,
              backgroundColor: muted
                ? alphaVar(colors.danger.base, 0.08)
                : "transparent",
              "&:hover": {
                backgroundColor: muted
                  ? alphaVar(colors.danger.base, 0.12)
                  : alphaVar(colors.gray[400], 0.06),
              },
            }}
          >
            {muted ? "Unmute" : "Mute"}
          </Button>

          <Button
            onClick={handleToggleHold}
            startIcon={
              isOnHold ? (
                <PlayArrowRounded sx={{ fontSize: "1rem" }} />
              ) : (
                <PauseRounded sx={{ fontSize: "1rem" }} />
              )
            }
            sx={{
              minWidth: 96,
              minHeight: 40,
              px: 1.5,
              color: colors.gray[700],
              fontWeight: 600,
              fontSize: "0.84rem",
              textTransform: "none",
              borderRadius: 2,
              backgroundColor: isOnHold
                ? alphaVar(colors.blue?.[500] ?? "#2563eb", 0.08)
                : "transparent",
              "&:hover": {
                backgroundColor: alphaVar(colors.blue?.[500] ?? "#2563eb", 0.08),
              },
            }}
          >
            {isOnHold ? "Resume" : "Hold"}
          </Button>

          <Button
            onClick={onOpenTransfer}
            startIcon={<PhoneForwardedRounded sx={{ fontSize: "1rem" }} />}
            sx={{
              minWidth: 110,
              minHeight: 40,
              px: 1.5,
              color: colors.blue?.[600] ?? "#2563eb",
              fontWeight: 600,
              fontSize: "0.84rem",
              textTransform: "none",
              borderRadius: 2,
              backgroundColor: isTransferOpen
                ? alphaVar(colors.blue?.[500] ?? "#2563eb", 0.08)
                : "transparent",
              "&:hover": {
                backgroundColor: alphaVar(colors.blue?.[500] ?? "#2563eb", 0.08),
              },
            }}
          >
            Transfer
          </Button>
        </Stack>

        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Button
            onClick={onHangupCall}
            variant="contained"
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              minWidth: 0,
              bgcolor: "#E53935",
              boxShadow: "0px 4px 10px rgba(229, 57, 53, 0.28)",
              "&:hover": { bgcolor: "#D32F2F" },
            }}
          >
            <CallEnd sx={{ fontSize: 30 }} />
          </Button>
        </Box>
      </Box>

      <Popover
        open={isTransferOpen}
        anchorEl={transferAnchorEl}
        onClose={onCloseTransfer}
        container={document.body}
        slotProps={{
          root: {
            sx: {
              zIndex: 2000000000,
            },
          },
          paper: {
            sx: {
              ml: -52,
              zIndex: 2000000000,
              width: 320,
              maxWidth: "calc(100vw - 32px)",
              borderRadius: 3,
              p: 1.5,
              boxShadow: "0px 12px 36px rgba(15, 23, 42, 0.16)",
            },
          },
        }}
      >
        <TransferPanel onClose={onCloseTransfer} />
      </Popover>
    </>
  );
};